import React, { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  ModelScopeFactory,
  type Model,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { cn } from '@cotera/client/v0/lib/utils';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { ResourceSelectionModel } from './selection/resource-selection.model';
import {
  PageBarSidebarTrigger,
  PageNavStrip,
} from '@cotera/client/v0/components/ui/page-bar-sidebar-trigger';
import { ContentScrollProvider } from '../shell/content-scroll-context';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import type { WatchablesFactory } from '../watchables/watchables-factory';
import { ResourcePageShell, type BreadcrumbItem } from './resource-page-shell';

/**
 * Factory function that creates models for the list page scope.
 */
export type CreateListPageModels = () => Model[];

/**
 * Shell layout for resource list pages.
 *
 * Wraps the common pattern used across list pages (chats, agents, triggers, etc):
 * - Sidebar trigger on mobile
 * - Outer background container with max-width content area
 * - ModelScopeFactory with ResourceSelectionModel
 *
 * A list scoped to somewhere in the workspace (a folder's chat history) passes
 * `breadcrumbs` and gets `ResourcePageShell`'s bar instead of the bare mobile
 * trigger — a top-level list (all chats, all agents) is its own destination and
 * has no trail to draw.
 */
export const ListPageShell: React.FC<{
  children: React.ReactNode;
  createModels: CreateListPageModels;
  deps?: React.DependencyList;
  /**
   * Classes on the content column — its width cap, and gutters when the page
   * wants more than `ResourceListView`'s built-in `px-8`. Defaults to the
   * `max-w-4xl` a list of names wants; pass a wider cap for a list whose rows
   * carry real columns beyond the name (Automations carries status, two timings
   * and an actions slot).
   */
  contentClassName?: string;
  /**
   * Trail to the list, for a list that sits under something. Omit it (the
   * default) for a top-level list and no bar is drawn; pass `'loading'` while
   * the trail is still resolving to hold a skeleton in its place, rather than
   * flashing a placeholder name or letting the bar appear late and shift the
   * rows down.
   */
  breadcrumbs?: BreadcrumbItem[] | 'loading';
}> = ({
  children,
  createModels,
  deps = [],
  contentClassName = 'max-w-4xl',
  breadcrumbs,
}) => {
  const content = (
    <div className="flex-1 min-h-0">
      <div className={cn('mx-auto h-full', contentClassName)}>
        <ModelScopeFactory createModels={createModels} deps={deps}>
          {children}
        </ModelScopeFactory>
      </div>
    </div>
  );

  if (breadcrumbs !== undefined) {
    // `ResourcePageShell` brings its own panel, mobile trigger and scroll
    // provider, so this branch adds none of them a second time.
    return (
      <ResourcePageShell
        breadcrumbs={breadcrumbs === 'loading' ? undefined : breadcrumbs}
      >
        {content}
      </ResourcePageShell>
    );
  }

  return (
    <div className="h-full w-full flex flex-col min-h-0">
      {/* See the note in `coco-chat-new.tsx`: the shell fills in a nav strip
          for a page that draws none, and only `PageBarSidebarTrigger` tells it
          this page already has one. */}
      <PageNavStrip>
        <PageBarSidebarTrigger />
      </PageNavStrip>

      <div className="v1-content-panel">
        <ContentScrollProvider>{content}</ContentScrollProvider>
      </div>
    </div>
  );
};

/**
 * Hook that provides the standard model creation pattern for list pages.
 * Creates a list page model plus ResourceSelectionModel.
 */
export function useListPageModelFactory<TModel extends Model>({
  createModel,
}: {
  createModel: (deps: {
    client: ReturnType<typeof useTenantedClient>;
    urlAdapter: ReturnType<typeof useUrlAdapter>;
    queryClient: QueryClient;
    orgId: string;
    watchablesFactory: WatchablesFactory;
  }) => TModel;
}): {
  createModels: CreateListPageModels;
  deps: [
    ReturnType<typeof useTenantedClient>,
    ReturnType<typeof useUrlAdapter>,
    QueryClient,
    string,
    WatchablesFactory
  ];
} {
  const client = useTenantedClient();
  const urlAdapter = useUrlAdapter();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const watchablesFactory = useWatchablesFactory();

  const createModels = useCallback(
    (): Model[] => [
      createModel({
        client,
        urlAdapter,
        queryClient,
        orgId,
        watchablesFactory,
      }),
      new ResourceSelectionModel(),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, urlAdapter, queryClient, orgId, watchablesFactory]
  );

  return {
    createModels,
    deps: [client, urlAdapter, queryClient, orgId, watchablesFactory],
  };
}
